
import { PAYMENT_HOLIDAYS } from '@ld.consumer-portal/constants';

export const AUTOPAY_UTILS_TRANSLATION_KEYS = {
  statusLoanPaidInFull: 'servicesStatusLoanPaidInFull',
  statusLoanTransferredGeneric: 'servicesStatusTransferred',

  errorAutopayHelocNotEligible: 'servicesErrorsAutopayHelocNotEligible',
  errorAutopayHelocOnTime: 'servicesErrorsAutopayHelocOnTime',
  errorAutopayNotAvailable: 'servicesErrorsAutopayNotAvailable',
  errorAutopayBiweeklyPastDue: 'servicesErrorsAutopayBiweeklyPastDue',
  errorPaymentReinstatementExceeded: 'paymentAmountRepaymentAdditionalHint',
  errorPaymentOutstandingFees: 'servicesErrorsPaymentOutstandingFees',
  errorAutopayDateConflict: 'servicesErrorsAutopayDateConflict',
  errorAutopayForbearance: 'servicesErrorsAutopayForbearance',
  errorAutopayForeclosure: 'servicesErrorsAutopayForeclosure',
  errorAutopayBankruptcy: 'servicesErrorsAutopayBankruptcy',
  errorAutopayLossMitigation: 'servicesErrorsAutopayLossMitigation',
  errorAutopayBiweeklyDelinquent: 'servicesErrorsAutopayBiweeklyDelinquent',

  warningAutopayOtpConflict: 'servicesWarningsAutopayOtpConflict',

  statusForbearance: 'servicesStatusForbearance',
  statusForeclosure: 'servicesStatusForeclosure',
  statusBankruptcy: 'servicesStatusBankruptcy',
  statusLossMitigation: 'servicesStatusLossMitigation',
  statusDelinquent: 'servicesStatusDelinquent',
  statusTransferred: 'servicesStatusTransferred',
} as const;

export function isHelocPastDueOrDelinquent(daysPastDueStatus?: string | null): boolean {
  const status = daysPastDueStatus?.toLowerCase() ?? '';
  return status.includes('past due') || status.includes('delinquent');
}

export function isTimeBefore(timeZone: string, cutoffHour: number): boolean {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hour12: false,
  });
  const hour = parseInt(formatter.format(now), 10);
  return hour < cutoffHour;
}

export function isTimeBefore6pm(timeZone: string): boolean {
  return isTimeBefore(timeZone, 18);
}

export function isTimeBefore6pmPST(): boolean {
  return isTimeBefore6pm('America/Los_Angeles');
}

export function getCalendarDateInTimeZone(date: Date, timeZone: string): Date {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return new Date(year, month - 1, day);
}

export function getTodayInTimeZone(timeZone: string): Date {
  return getCalendarDateInTimeZone(new Date(), timeZone);
}

export function getTodayInPST(): Date {
  return getTodayInTimeZone('America/Los_Angeles');
}

export function parseCalendarDate(dateStr: string): Date {
  const trimmed = dateStr.trim();

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(trimmed);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(trimmed);
  if (usMatch) {
    return new Date(Number(usMatch[3]), Number(usMatch[1]) - 1, Number(usMatch[2]));
  }

  const parsed = new Date(trimmed);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function isOtpEditDeleteTimeEligible(paymentDateStr: string): boolean {
  const paymentDate = parseCalendarDate(paymentDateStr);
  const todayPst = getTodayInPST();

  if (paymentDate.getTime() > todayPst.getTime()) {
    return true;
  }

  if (paymentDate.getTime() === todayPst.getTime()) {
    return isTimeBefore6pmPST();
  }

  return false;
}

export function msUntilPstCutoff(): number {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(now);
  const h = Number(parts.find((p) => p.type === 'hour')?.value) % 24;
  const m = Number(parts.find((p) => p.type === 'minute')?.value);
  const s = Number(parts.find((p) => p.type === 'second')?.value);
  if (h >= 18) return 0;
  return (18 - h) * 3_600_000 - m * 60_000 - s * 1_000;
}

export function isTimeBefore6pmCST(): boolean {
  return isTimeBefore6pm('America/Chicago');
}

export function isTimeBefore8pmCST(): boolean {
  return isTimeBefore('America/Chicago', 20);
}

export function addBusinessDays(startDate: Date, days: number, holidayDates: Date[]): Date {
  const result = new Date(startDate);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result) && !isHoliday(result, holidayDates)) {
      added++;
    }
  }
  return result;
}

export function getAutopayMinPaymentDate(): Date {
  const holidayDates = parseHolidayDates();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const businessDaysToAdd = isTimeBefore8pmCST() ? 2 : 3;
  return addBusinessDays(today, businessDaysToAdd, holidayDates);
}

export function isCurrentCSTDateBeforeOrEqualEligibleDate(eligibleDateStr: string): boolean {
  const currentCst = new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' });
  const cstDate = new Date(currentCst);
  const eligibleDate = new Date(eligibleDateStr);

  cstDate.setHours(0, 0, 0, 0);
  eligibleDate.setHours(0, 0, 0, 0);

  return cstDate <= eligibleDate;
}

export function isAutopayEditDeleteAllowed(paymentCancellationCutoffDate: string | null | undefined): boolean {
  if (!paymentCancellationCutoffDate) {
    return true;
  }

  const currentCst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const todayOnly = new Date(currentCst.getFullYear(), currentCst.getMonth(), currentCst.getDate());

  const cutoff = new Date(new Date(paymentCancellationCutoffDate).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const cutoffOnly = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate());

  if (cutoffOnly > todayOnly) {
    return true;
  }

  if (cutoffOnly.getTime() === todayOnly.getTime()) {
    return isTimeBefore8pmCST();
  }

  return false;
}

export function isAutopaySetupAllowedAfterDeletion(lastDeletedAutopayDate: string | null | undefined): boolean {
  if (!lastDeletedAutopayDate) {
    return true;
  }

  const holidayDates = parseHolidayDates();
  const deletedDateCST = new Date(new Date(lastDeletedAutopayDate).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const deletedDateOnly = new Date(deletedDateCST.getFullYear(), deletedDateCST.getMonth(), deletedDateCST.getDate());

  const nowCST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));

  const nextCalendarDay = new Date(deletedDateOnly);
  nextCalendarDay.setDate(nextCalendarDay.getDate() + 1);

  const deletedOnNonBusinessDay = isWeekend(deletedDateOnly) || isHoliday(deletedDateOnly, holidayDates);
  const nextDayIsNonBusinessDay = isWeekend(nextCalendarDay) || isHoliday(nextCalendarDay, holidayDates);

  let eligibleDate: Date;

  if (deletedOnNonBusinessDay) {
    eligibleDate = addBusinessDays(getNextBusinessDay(deletedDateOnly, holidayDates), 1, holidayDates);
  } else if (nextDayIsNonBusinessDay) {
    if (deletedDateCST.getHours() < 22) {
      eligibleDate = nextCalendarDay;
    } else {
      eligibleDate = addBusinessDays(getNextBusinessDay(nextCalendarDay, holidayDates), 1, holidayDates);
    }
  } else {
    eligibleDate = nextCalendarDay;
  }

  eligibleDate.setHours(0, 0, 0, 0);

  return nowCST >= eligibleDate;
}

function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function isHoliday(date: Date, holidayDates: Date[]): boolean {
  return holidayDates.some(
    (h) => h.getFullYear() === date.getFullYear() && h.getMonth() === date.getMonth() && h.getDate() === date.getDate(),
  );
}

export function getNextBusinessDay(date: Date, holidayDates: Date[]): Date {
  const result = new Date(date);
  while (isWeekend(result) || isHoliday(result, holidayDates)) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

export function isTodayHolidayOrWeekend(): boolean {
  const holidayDates = parseHolidayDates();
  const currentCST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return isWeekend(currentCST) || isHoliday(currentCST, holidayDates);
}

export function isDateHolidayOrWeekend(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const holidayDates = parseHolidayDates();
  const date = new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return isWeekend(date) || isHoliday(date, holidayDates);
}

export function isNextDayAfterDateNonBusinessDay(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const holidayDates = parseHolidayDates();
  const date = new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const nextDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return isWeekend(nextDay) || isHoliday(nextDay, holidayDates);
}

export function wasDeletionBeforeCutoff(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const date = new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return date.getHours() < 22;
}

function parseHolidayDates(): Date[] {
  return PAYMENT_HOLIDAYS.map((dateStr) => {
    const [month, day, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  });
}

export function isAutopayDeleteAllowedAfterSetup(createdAt: string | null | undefined): boolean {
  if (!createdAt) {
    return true;
  }

  const holidayDates = parseHolidayDates();

  const setupDateCST = new Date(new Date(createdAt).toLocaleString('en-US', { timeZone: 'America/Chicago' }));

  if (setupDateCST.getHours() >= 22) {
    setupDateCST.setDate(setupDateCST.getDate() + 1);
  }

  const nextBusinessDate = getNextBusinessDay(setupDateCST, holidayDates);

  const thresholdDate = new Date(nextBusinessDate);
  thresholdDate.setDate(thresholdDate.getDate() + 1);
  thresholdDate.setHours(0, 0, 0, 0);

  const currentCST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));

  return currentCST >= thresholdDate;
}

export function getCutoffTimeDisplay(timezone: 'PST' | 'CST' = 'CST'): string {
  const tz = timezone === 'PST' ? 'America/Los_Angeles' : 'America/Chicago';
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(now);
  const tzAbbr = parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
  return `6:00 PM ${tzAbbr}`;
}

export function isCurrentDateGreaterOrEqualToPaymentDate(paymentDateStr: string): boolean {
  const currentDate = new Date();
  const paymentDate = new Date(paymentDateStr);

  currentDate.setHours(0, 0, 0, 0);
  paymentDate.setHours(0, 0, 0, 0);

  return currentDate >= paymentDate;
}

export function getBusinessDaysBetween(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);

  while (current < endDate) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

export interface AutopayEligibilityResult {
  isEligible: boolean;
  eligibilityType: 'inhouse' | 'repay' | 'none';
  reason?: string;
  canSetupMonthly: boolean;
  canSetupBiweekly: boolean;
}

export function checkAccountAutopayEligibility(account: {
  type: string;
  daysPastDueStatus?: string;
  recurringPaymentEligibility?: boolean;
  biweeklyRecurringPaymentEligibility?: boolean;
  isInhouseAutopayEnabled?: boolean;
  isRepayAutopayEnabled?: boolean;
  accountStanding?: string;
  isPaidInFull?: boolean;
  isTransferredToCenlar?: boolean;
  isTransferredToThirdParty?: boolean;
}): AutopayEligibilityResult {
  if (account.isPaidInFull) {
    return {
      isEligible: false,
      eligibilityType: 'none',
      reason: 'This loan has been paid in full.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (account.isTransferredToCenlar || account.isTransferredToThirdParty) {
    return {
      isEligible: false,
      eligibilityType: 'none',
      reason: 'This loan has been transferred to another servicer.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (account.type === 'HELOC') {
    if (account.isInhouseAutopayEnabled) {
      return {
        isEligible: true,
        eligibilityType: 'inhouse',
        canSetupMonthly: account.recurringPaymentEligibility ?? true,
        canSetupBiweekly: false,
      };
    }
    if (account.isRepayAutopayEnabled) {
      return {
        isEligible: true,
        eligibilityType: 'repay',
        canSetupMonthly: true,
        canSetupBiweekly: false,
      };
    }
    return {
      isEligible: false,
      eligibilityType: 'none',
      reason: 'Autopay is not available for this HELOC loan.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (account.isInhouseAutopayEnabled && account.recurringPaymentEligibility) {
    return {
      isEligible: true,
      eligibilityType: 'inhouse',
      canSetupMonthly: true,
      canSetupBiweekly: account.biweeklyRecurringPaymentEligibility ?? false,
    };
  }

  if (account.isRepayAutopayEnabled) {
    return {
      isEligible: true,
      eligibilityType: 'repay',
      canSetupMonthly: true,
      canSetupBiweekly: false,
    };
  }

  return {
    isEligible: false,
    eligibilityType: 'none',
    reason: 'Autopay is not available for this loan.',
    canSetupMonthly: false,
    canSetupBiweekly: false,
  };
}

export interface RepayPortalConfig {
  baseUrl: string;
  autopayPath: string;
  paymentPath: string;
}

export const DEFAULT_REPAY_CONFIG: RepayPortalConfig = {
  baseUrl: 'https://repay.loandepot.com',
  autopayPath: '/autopay',
  paymentPath: '/payment',
};

export function buildRepayPortalUrl(
  config: RepayPortalConfig,
  loanNumber: string,
  type: 'autopay' | 'payment',
  returnUrl?: string,
): string {
  const path = type === 'autopay' ? config.autopayPath : config.paymentPath;
  const url = new URL(`${config.baseUrl}${path}`);

  url.searchParams.set('loanNumber', loanNumber);

  if (returnUrl) {
    url.searchParams.set('returnUrl', returnUrl);
  }

  return url.toString();
}

export function getAutopayRoute(
  eligibility: AutopayEligibilityResult,
  loanNumber: string,
  repayConfig?: RepayPortalConfig,
): { type: 'internal' | 'external'; path: string } {
  if (!eligibility.isEligible) {
    return { type: 'internal', path: `/loan/${loanNumber}` };
  }

  if (eligibility.eligibilityType === 'inhouse') {
    return { type: 'internal', path: `/loan/${loanNumber}/autopay` };
  }

  if (eligibility.eligibilityType === 'repay') {
    const config = repayConfig ?? DEFAULT_REPAY_CONFIG;
    const repayUrl = buildRepayPortalUrl(config, loanNumber, 'autopay');
    return { type: 'external', path: repayUrl };
  }

  return { type: 'internal', path: `/loan/${loanNumber}` };
}

export function shouldRedirectToRepay(account: {
  type: string;
  isInhouseAutopayEnabled?: boolean;
  isRepayAutopayEnabled?: boolean;
  recurringPaymentEligibility?: boolean;
}): boolean {
  if (account.type === 'HELOC') {
    return account.isRepayAutopayEnabled ?? false;
  }

  if (!account.isInhouseAutopayEnabled || !account.recurringPaymentEligibility) {
    return account.isRepayAutopayEnabled ?? false;
  }

  return false;
}

export function canMakeAdditionalPrincipalPayment(outstandingFees: number): boolean {
  return outstandingFees <= 0;
}

export function checkPastDueEligibility(
  isPastDue: boolean,
  daysPastDue = 0,
): { canSetupMonthly: boolean; canSetupBiweekly: boolean; reason?: string } {
  if (isPastDue || daysPastDue > 0) {
    return {
      canSetupMonthly: true, // Monthly is still allowed when past due
      canSetupBiweekly: false,
      reason: 'Bi-Weekly Payment Plan is not allowed when account is past due.',
    };
  }

  return {
    canSetupMonthly: true,
    canSetupBiweekly: true,
  };
}

export function validateAdditionalPrincipalAmount(
  additionalPrincipal: number,
  totalReinstatementAmount: number,
): { isValid: boolean; errorMessage?: string } {
  if (additionalPrincipal > totalReinstatementAmount && totalReinstatementAmount > 0) {
    return {
      isValid: false,
      errorMessage: 'Please enter an amount less than the total reinstatement amount.',
    };
  }
  return { isValid: true };
}

export interface AutopaySetupValidation {
  canProceed: boolean;
  errors: string[];
  warnings: string[];
}

export function validateAutopaySetup(params: {
  outstandingFees: number;
  isPastDue: boolean;
  daysPastDue?: number;
  selectedFrequency: 'monthly' | 'biweekly';
  additionalPrincipal?: number;
  totalReinstatementAmount?: number;
  selectedPaymentDate?: Date;
  pendingOtpDates?: Date[];
  existingAutopayDate?: Date;
}): AutopaySetupValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (params.additionalPrincipal && params.additionalPrincipal > 0) {
    if (!canMakeAdditionalPrincipalPayment(params.outstandingFees)) {
      errors.push('Outstanding fees on the loan must be paid before additional principal payments are allowed.');
    }

    if (params.totalReinstatementAmount) {
      const validation = validateAdditionalPrincipalAmount(params.additionalPrincipal, params.totalReinstatementAmount);
      if (!validation.isValid && validation.errorMessage) {
        errors.push(validation.errorMessage);
      }
    }
  }

  if (params.selectedFrequency === 'biweekly') {
    const pastDueCheck = checkPastDueEligibility(params.isPastDue, params.daysPastDue);
    if (!pastDueCheck.canSetupBiweekly && pastDueCheck.reason) {
      errors.push(pastDueCheck.reason);
    }
  }

  if (params.selectedPaymentDate && params.pendingOtpDates && params.pendingOtpDates.length > 0) {
    const conflictingOtp = checkOtpConflict(params.selectedPaymentDate, params.pendingOtpDates);
    if (conflictingOtp) {
      warnings.push(
        `You have a one-time payment scheduled for ${conflictingOtp.toLocaleDateString()}. ` +
          'Your autopay will also draft on this date.',
      );
    }
  }

  if (params.selectedPaymentDate && params.existingAutopayDate) {
    if (isSameDate(params.selectedPaymentDate, params.existingAutopayDate)) {
      errors.push(
        'The selected payment date conflicts with an existing scheduled payment. ' + 'Please select a different date.',
      );
    }
  }

  return {
    canProceed: errors.length === 0,
    errors,
    warnings,
  };
}

export function checkOtpConflict(autopayDate: Date, otpDates: Date[]): Date | null {
  for (const otpDate of otpDates) {
    if (isSameDate(autopayDate, otpDate)) {
      return otpDate;
    }
  }
  return null;
}

export function isAutopayDeleteBlockedByCutoff(nextPaymentDate: Date): boolean {
  const nowCST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const todayCST = new Date(nowCST.getFullYear(), nowCST.getMonth(), nowCST.getDate());
  const paymentDateOnly = new Date(nextPaymentDate.getFullYear(), nextPaymentDate.getMonth(), nextPaymentDate.getDate());

  if (todayCST.getTime() !== paymentDateOnly.getTime()) {
    return false;
  }

  return !isTimeBefore8pmCST();
}

export function isSameDate(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function getBiweeklyPaymentDates(startDate: Date, monthsAhead = 3): Date[] {
  const dates: Date[] = [];
  const currentDate = new Date(startDate);

  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + monthsAhead);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 14);
  }

  return dates;
}

export function checkBiweeklyOtpConflicts(autopayStartDate: Date, otpDates: Date[], monthsAhead = 3): Date[] {
  const biweeklyDates = getBiweeklyPaymentDates(autopayStartDate, monthsAhead);
  const conflicts: Date[] = [];

  for (const otpDate of otpDates) {
    for (const biweeklyDate of biweeklyDates) {
      if (isSameDate(otpDate, biweeklyDate)) {
        conflicts.push(otpDate);
        break;
      }
    }
  }

  return conflicts;
}

export type SpecialLoanStatus =
  | 'FORBEARANCE'
  | 'FORECLOSURE'
  | 'BANKRUPTCY'
  | 'LOSS_MITIGATION'
  | 'DELINQUENT'
  | 'TRANSFERRED';

export interface LoanStatusCheckResult {
  isEligible: boolean;
  status?: SpecialLoanStatus;
  message?: string;
  canSetupMonthly: boolean;
  canSetupBiweekly: boolean;
}

export function checkSpecialLoanStatus(loan: {
  isForbearance?: boolean;
  isForeclosure?: boolean;
  isBankruptcy?: boolean;
  isLossMitigation?: boolean;
  delinquencyStatus?: string;
  daysPastDue?: number;
  isTransferred?: boolean;
}): LoanStatusCheckResult {
  if (loan.isForbearance) {
    return {
      isEligible: false,
      status: 'FORBEARANCE',
      message:
        'Autopay is not available while your loan is in forbearance. Please contact Customer Service for payment options.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (loan.isForeclosure) {
    return {
      isEligible: false,
      status: 'FORECLOSURE',
      message: 'Autopay is not available for loans in foreclosure. Please contact Customer Service for assistance.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (loan.isBankruptcy) {
    return {
      isEligible: false,
      status: 'BANKRUPTCY',
      message: 'Autopay is not available for loans with active bankruptcy. Please contact Customer Service.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (loan.isLossMitigation) {
    return {
      isEligible: false,
      status: 'LOSS_MITIGATION',
      message: 'Autopay is not available while your loan is in loss mitigation. Please contact Customer Service.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  if (loan.daysPastDue && loan.daysPastDue > 0) {
    return {
      isEligible: true,
      status: 'DELINQUENT',
      message: 'Bi-weekly autopay is not available while your account is past due. Monthly autopay is still available.',
      canSetupMonthly: true,
      canSetupBiweekly: false,
    };
  }

  if (loan.isTransferred) {
    return {
      isEligible: false,
      status: 'TRANSFERRED',
      message:
        'This loan has been transferred to another servicer. Please contact your new servicer for autopay options.',
      canSetupMonthly: false,
      canSetupBiweekly: false,
    };
  }

  return {
    isEligible: true,
    canSetupMonthly: true,
    canSetupBiweekly: true,
  };
}

export function getSpecialStatusMessage(status: SpecialLoanStatus): string {
  const messages: Record<SpecialLoanStatus, string> = {
    FORBEARANCE: 'Autopay is not available during forbearance.',
    FORECLOSURE: 'Autopay is not available for loans in foreclosure.',
    BANKRUPTCY: 'Autopay is not available for loans with active bankruptcy.',
    LOSS_MITIGATION: 'Autopay is not available during loss mitigation.',
    DELINQUENT: 'Bi-weekly autopay is not available while past due.',
    TRANSFERRED: 'This loan has been transferred to another servicer.',
  };
  return messages[status];
}
